import { supabase } from '../config/supabase';
import { RobustSupabaseClient } from './RobustSupabaseClient';
import { ErrorLoggerService } from './ErrorLoggerService';

export interface UserSession {
  id: string;
  user_id: string;
  session_start: string;
  session_end: string | null;
  last_activity: string;
  ip_address: string | null;
  user_agent: string;
  is_active: boolean;
  created_at: string;
  user_email?: string;
}

export interface SessionStats {
  total_active: number;
  total_today: number;
  average_duration: number;
  unique_users_today: number;
}

export class SessionService {
  private static currentSessionId: string | null = null;
  private static activityInterval: NodeJS.Timeout | null = null;

  /**
   * Start a new session for the current user - WITH ROBUST RETRY
   */
  static async startSession(userId: string, userEmail: string): Promise<string | null> {
    // 🔧 FIX: Guard against duplicate sessions.
    // onAuthStateChange, checkAuth, and handleLoginSuccess can all fire in quick
    // succession after login — prevent creating multiple rows in user_sessions.
    if (this.currentSessionId) {
      console.log('📊 Session already active, skipping duplicate:', this.currentSessionId);
      return this.currentSessionId;
    }

    try {
      const { data, error } = await RobustSupabaseClient.insert(
        'user_sessions',
        {
          user_id: userId,
          user_email: userEmail,
          user_agent: navigator.userAgent,
          is_active: true
        },
        {
          enableRetry: true,
          maxRetries: 3,
          enableCircuitBreaker: true,
          circuitBreakerName: 'session-start',
          componentName: 'SessionService',
          actionName: 'startSession'
        }
      );

      if (error) {
        console.error('Error starting session:', error);
        await ErrorLoggerService.logDatabaseError(
          `Failed to start session for user ${userEmail}: ${error.message}`,
          'user_sessions',
          'INSERT',
          'SessionService'
        );
        return null;
      }

      if (!(data as any)?.id) {
        console.error('Session insert returned no id');
        return null;
      }

      this.currentSessionId = (data as any).id;
      this.startActivityTracking();

      return (data as any).id;
    } catch (error: any) {
      console.error('Error starting session:', error);
      await ErrorLoggerService.logRuntimeError(error, 'SessionService', 'startSession');
      return null;
    }
  }

  /**
   * Update session activity (heartbeat) - WITH ROBUST RETRY
   */
  static async updateActivity(): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      await RobustSupabaseClient.update(
        'user_sessions',
        { last_activity: new Date().toISOString() },
        { id: this.currentSessionId },
        {
          enableRetry: true,
          maxRetries: 2,
          timeoutMs: 5000, // Quick timeout for heartbeat
          enableCircuitBreaker: false, // No circuit breaker for heartbeat
          componentName: 'SessionService',
          actionName: 'updateActivity'
        }
      );
    } catch (error: any) {
      // Silent failure for heartbeat - don't spam logs
      console.warn('Warning: Failed to update session activity:', error);
    }
  }

  /**
   * Start automatic activity tracking (every 30 seconds)
   */
  private static startActivityTracking(): void {
    if (this.activityInterval) {
      clearInterval(this.activityInterval);
    }

    // Update activity every 30 seconds
    this.activityInterval = setInterval(() => {
      this.updateActivity();
    }, 30000);

    // Also update on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.updateActivity();
      }
    });
  }

  /**
   * End current session - WITH ROBUST RETRY
   */
  static async endSession(): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      await RobustSupabaseClient.update(
        'user_sessions',
        {
          session_end: new Date().toISOString(),
          is_active: false
        },
        { id: this.currentSessionId },
        {
          enableRetry: true,
          maxRetries: 3,
          enableCircuitBreaker: true,
          circuitBreakerName: 'session-end',
          componentName: 'SessionService',
          actionName: 'endSession'
        }
      );

      if (this.activityInterval) {
        clearInterval(this.activityInterval);
        this.activityInterval = null;
      }

      this.currentSessionId = null;
    } catch (error: any) {
      console.error('Error ending session:', error);
      await ErrorLoggerService.logRuntimeError(error, 'SessionService', 'endSession');
    }
  }

  /**
   * Get all active sessions (for admin) - WITH ROBUST RPC & CACHING
   */
  static async getActiveSessions(): Promise<UserSession[]> {
    try {
      const { data, error } = await RobustSupabaseClient.rpc(
        'get_active_sessions',
        undefined,
        {
          enableRetry: true,
          maxRetries: 3,
          enableCache: true,
          cacheTtlMs: 10000, // 10 seconds cache for live data
          cacheKey: 'active-sessions',
          componentName: 'SessionService',
          actionName: 'getActiveSessions'
        }
      );

      if (error) {
        console.error('Error fetching active sessions:', error);
        await ErrorLoggerService.logDatabaseError(
          `Failed to fetch active sessions: ${error.message}`,
          'get_active_sessions',
          'RPC',
          'SessionService'
        );
        return [];
      }

      return (data as any) || [];
    } catch (error: any) {
      console.error('Error fetching active sessions:', error);
      await ErrorLoggerService.logRuntimeError(error, 'SessionService', 'getActiveSessions');
      return [];
    }
  }

  /**
   * Get session statistics (for admin) - WITH ROBUST RPC & CACHING
   */
  static async getSessionStats(): Promise<SessionStats> {
    try {
      const { data, error } = await RobustSupabaseClient.rpc(
        'get_session_stats',
        undefined,
        {
          enableRetry: true,
          maxRetries: 3,
          enableCache: true,
          cacheTtlMs: 30000, // 30 seconds cache for stats
          cacheKey: 'session-stats',
          componentName: 'SessionService',
          actionName: 'getSessionStats'
        }
      );

      if (error) {
        console.error('Error fetching session stats:', error);
        await ErrorLoggerService.logDatabaseError(
          `Failed to fetch session stats: ${error.message}`,
          'get_session_stats',
          'RPC',
          'SessionService'
        );
        return {
          total_active: 0,
          total_today: 0,
          average_duration: 0,
          unique_users_today: 0
        };
      }

      return (data as any) || {
        total_active: 0,
        total_today: 0,
        average_duration: 0,
        unique_users_today: 0
      };
    } catch (error: any) {
      console.error('Error fetching session stats:', error);
      await ErrorLoggerService.logRuntimeError(error, 'SessionService', 'getSessionStats');
      return {
        total_active: 0,
        total_today: 0,
        average_duration: 0,
        unique_users_today: 0
      };
    }
  }

  /**
   * Get session duration in minutes
   */
  static getSessionDuration(session: UserSession): number {
    const start = new Date(session.session_start);
    const end = session.session_end ? new Date(session.session_end) : new Date();
    return Math.floor((end.getTime() - start.getTime()) / 1000 / 60);
  }

  /**
   * Get time since last activity
   */
  static getTimeSinceActivity(lastActivity: string): string {
    const now = new Date();
    const activity = new Date(lastActivity);
    const diffMs = now.getTime() - activity.getTime();
    const diffMins = Math.floor(diffMs / 1000 / 60);

    if (diffMins < 1) return 'Proprio ora';
    if (diffMins === 1) return '1 minuto fa';
    if (diffMins < 60) return `${diffMins} minuti fa`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 ora fa';
    return `${diffHours} ore fa`;
  }

  /**
   * Parse user agent to get browser/device info
   */
  static parseUserAgent(userAgent: string): { browser: string; device: string } {
    const ua = userAgent.toLowerCase();

    // Browser detection
    let browser = 'Unknown';
    if (ua.includes('chrome') && !ua.includes('edg')) browser = 'Chrome';
    else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari';
    else if (ua.includes('firefox')) browser = 'Firefox';
    else if (ua.includes('edg')) browser = 'Edge';
    else if (ua.includes('opera') || ua.includes('opr')) browser = 'Opera';

    // Device detection
    let device = 'Desktop';
    if (ua.includes('mobile')) device = 'Mobile';
    else if (ua.includes('tablet') || ua.includes('ipad')) device = 'Tablet';

    return { browser, device };
  }
}
